import { createSelector } from 'reselect'
import { buildDraftDefaults } from 'ducks/drafts2/util'
import { selectCurrentTicketId } from 'selectors/tickets/current'
import { selectRawTicket } from 'selectors/tickets/byId'
import { emptyObj } from 'util/objects'
import { emptyArr } from 'util/arrays'
import { selectChangesetsForTicketId } from 'selectors/currentChangesets'

const storeKey = 'drafts2'

export const selectById = state => state[storeKey].byId || emptyObj

export const selectRawDraftsByTicketId = state => {
  return state[storeKey]?.byTicketId || emptyObj
}

export const selectByMessageId = state =>
  state[storeKey].byMessageId || emptyObj

export const selectDraftIdByTicketId = (state, ticketId, type) => {
  const byTicketId = selectRawDraftsByTicketId(state)
  const draftIdSet = byTicketId[ticketId]
  return draftIdSet ? draftIdSet[type] : null
}

export const selectDraftByTicketId = (state, ticketId, type) => {
  const draftId = selectDraftIdByTicketId(state, ticketId, type)
  if (draftId) return selectById(state)[draftId] || null
  return null
}

export const selectDraftBodyByTicketId = (state, ticketId, type) => {
  const found = selectDraftByTicketId(state, ticketId, type)
  return found ? found.body : null
}

export const selectDraftById = (state, draftId) => {
  return selectById(state)[draftId] || null
}

export const selectDraftBodyByDraftId = (state, draftId) => {
  const found = selectDraftById(state, draftId)
  return found ? found.body : null
}

export const selectDraftPropByDraftId = (state, draftId, prop) => {
  const found = selectDraftById(state, draftId)
  return found ? found[prop] : null
}

export const selectAttachmentsByDraftId = (state, draftId) => {
  const found = selectDraftById(state, draftId)
  return found ? found.attachmentsNormalized : emptyArr
}

export const selectDraftPropOrDefault = (state, draftId, ticketId, prop) => {
  const found = selectDraftById(state, draftId)
  if (found) return found[prop]

  const ticket = selectRawTicket(state, ticketId)
  const defaults = ticket.draftDefaults

  if (defaults) return defaults[prop]

  // (TA): There is a rare occurrence where ticket.draftDefaults not found for a ticket
  // (probably being overwritten or not being set in an obv action)
  // cannot find the redux action that's responsible for this so gonna try manually build the defaults
  const ticketChangesets = selectChangesetsForTicketId(state, ticketId)
  const rebuiltDraftDefaults = buildDraftDefaults(
    state,
    ticket,
    ticketChangesets
  )

  return rebuiltDraftDefaults ? rebuiltDraftDefaults[prop] : null
}

export const makeDraftPropOrDefaultSelector = (state, draftId, ticketId) => {
  return prop => selectDraftPropOrDefault(state, draftId, ticketId, prop)
}

export const selectDraftIsSynced = (state, draftId) => {
  const draft = selectDraftById(state, draftId)
  if (!draft) return null
  return draft.version === draft.serverVersion
}

const selectCurrentTicketDraftId = createSelector(
  selectCurrentTicketId,
  selectRawDraftsByTicketId,
  (ticketId, byTicketId) => byTicketId[ticketId]
)

export const selectCurrentTicketHasDraft = createSelector(
  selectCurrentTicketDraftId,
  draftId => !!draftId
)
